import { useState } from 'react';

import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Tab from '@zenbusiness/zui/Tab';
import TabContext from '@zenbusiness/zui/TabContext';
import TabList from '@zenbusiness/zui/TabList';
import TabPanel from '@zenbusiness/zui/TabPanel';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import DetailCardFields from '#client/components/DetailCardFields';
import FormStepModal from '#client/components/FormStepModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import { MODAL_NAMES } from '#client/constants/modals';
import useModalContext from '#client/hooks/useModalContext';
import StepItem from '#client/pages/DashboardNotFunded/components/StepItem';

import {
  DialogContentStyled,
  TabBoxStyled,
  TabWrapperStyled,
  WrapperStyled
} from './AddCardWalletModal.styles';

import { APPLE_WALLET_TAB, GOOGLE_PAY_TAB, stepsApplePay, stepsGooglePay } from './constants/note';

const descriptionNode = ({ showText, hideText, show, onClick }) => (
  <Stack
    alignItems="center"
    direction="row"
    gap="4px"
    mt="8px"
    onClick={onClick}
    sx={{ cursor: 'pointer' }}
  >
    {show ? <VisibilityOffIcon color="blue" /> : <RemoveRedEyeIcon color="blue" />}
    <Text
      color="blue"
      size="md"
      weight="medium"
    >
      {show ? hideText : showText}
    </Text>
  </Stack>
);

export default function AddCardWalletModal({ handleClose, isOpenCardModalAfterClose = true }) {
  const { openModal } = useModalContext();
  const [currentTab, setCurrentTab] = useState(APPLE_WALLET_TAB);

  const theme = useTheme();
  const [showCardDetailsApple, setShowCardDetailsApple] = useState(false);
  const [showCardDetailsGG, setShowCardDetailsGG] = useState(false);

  const toggleShowCardDetailsApple = () => {
    /**
     * Should only show 1 of the 2 card details, so as not to duplicate VGS data
     */
    setShowCardDetailsGG(false);
    setShowCardDetailsApple((prev) => !prev);
  };

  const toggleShowCardDetailsGG = () => {
    /**
     * Should only show 1 of the 2 card details, so as not to duplicate VGS data
     */
    setShowCardDetailsApple(false);
    setShowCardDetailsGG((prev) => !prev);
  };

  const handleCloseModal = () => {
    /**
     * Should hide all VGS data so they don't get duplicated and cause UI errors
     * Open the modal card again because we closed it before
     */
    setShowCardDetailsApple(false);
    setShowCardDetailsGG(false);
    if (isOpenCardModalAfterClose) {
      openModal({
        name: MODAL_NAMES.CARD_MODAL
      });
      setTimeout(() => {
        handleClose();
      }, 1000);

      return;
    }

    handleClose();
  };

  const handleTabChange = (event, newValue) => {
    setShowCardDetailsApple(false);
    setShowCardDetailsGG(false);
    setCurrentTab(newValue);
  };

  return (
    <FormStepModal
      handleClose={handleCloseModal}
      isHideBackButton
      title="Add card to digital wallet"
      titleBackground={theme.palette.background.grey.main}
    >
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <WrapperStyled mb={{ xs: '16px', md: '24px' }}>
              <Stack
                gap={{ xs: '16px', md: '24px' }}
                mt="40px"
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Be ready for expenses
                </Heading>
                <Text
                  color="secondary"
                  size="lg"
                >
                  Add your ZenBusiness debit card to your digital Wallet.
                </Text>
              </Stack>
            </WrapperStyled>
          </FocusGrid>
          <TabContext value={currentTab}>
            <FocusGrid>
              <WrapperStyled>
                <TabBoxStyled>
                  <TabList onChange={handleTabChange}>
                    <Tab
                      label="Apple Wallet"
                      value={APPLE_WALLET_TAB}
                    />
                    <Tab
                      label="Google Pay"
                      value={GOOGLE_PAY_TAB}
                    />
                  </TabList>
                </TabBoxStyled>
              </WrapperStyled>
            </FocusGrid>
            <FocusGrid>
              <TabWrapperStyled>
                <TabPanel
                  sx={{ px: 0, paddingTop: 0 }}
                  value={APPLE_WALLET_TAB}
                >
                  <Stack
                    gap="24px"
                    mt={{ xs: '32px', md: '64px' }}
                  >
                    <Stack gap={{ xs: '16px', md: '32px' }}>
                      <StepItem
                        description={descriptionNode({
                          showText: 'Show card details',
                          hideText: 'Hide card details',
                          show: showCardDetailsApple,
                          onClick: toggleShowCardDetailsApple
                        })}
                        key={1}
                        number={1}
                        title="Copy ZenBusiness Banking card, expiry, and CVV numbers."
                      />
                      {showCardDetailsApple && (
                        <Stack
                          gap="16px"
                          marginLeft={{ xs: '48px', md: '56px' }}
                        >
                          <DetailCardFields />
                        </Stack>
                      )}
                      {stepsApplePay.map((step) => (
                        <StepItem
                          description={step.description}
                          key={step.number}
                          number={step.number}
                          title={step.title}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </TabPanel>
                <TabPanel
                  sx={{ px: 0, paddingTop: 0 }}
                  value={GOOGLE_PAY_TAB}
                >
                  <Stack
                    gap="24px"
                    mt={{ xs: '32px', md: '64px' }}
                  >
                    <Stack gap={{ xs: '16px', md: '32px' }}>
                      <StepItem
                        description={descriptionNode({
                          showText: 'Show card details',
                          hideText: 'Hide card details',
                          show: showCardDetailsGG,
                          onClick: toggleShowCardDetailsGG
                        })}
                        key={1}
                        number={1}
                        title="Copy ZenBusiness Banking card, expiry, and CVV numbers."
                      />
                      {showCardDetailsGG && (
                        <Stack
                          gap="16px"
                          marginLeft={{ xs: '48px', md: '56px' }}
                        >
                          <DetailCardFields />
                        </Stack>
                      )}
                      {stepsGooglePay.map((step) => (
                        <StepItem
                          description={step.description}
                          key={step.number}
                          number={step.number}
                          title={step.title}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </TabPanel>
              </TabWrapperStyled>
            </FocusGrid>
          </TabContext>
        </ContainerGrid>
      </DialogContentStyled>
    </FormStepModal>
  );
}

AddCardWalletModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  isOpenCardModalAfterClose: PropTypes.bool
};
